import React, { useState, useEffect, useCallback } from 'react';
import { authAPI, visitsAPI } from '../utils/api';
import '../styles/PageTemplate.css';
import '../styles/Visits.css';

const fmt = (iso) => new Date(iso).toLocaleString('en-GB', {
  month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
});

// 来访持续多久；不到一分钟就不显示
const span = (a, b) => {
  const mins = Math.round((new Date(b) - new Date(a)) / 60000);
  return mins < 1 ? '' : mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)} h ${mins % 60} min`;
};

const host = (u) => { try { return new URL(u).hostname; } catch { return u; } };

// 简单条形图：不引图表库，宽度按最大值归一化
function Bars({ rows, label }) {
  if (!rows?.length) return <p className="vs-empty">—</p>;
  const max = Math.max(...rows.map(r => r.n));
  return (
    <ul className="vs-bars">
      {rows.map(r => (
        <li key={r.k}>
          <span className="vs-bar-k" title={r.k}>{r.k || '(empty)'}</span>
          <span className="vs-bar-track">
            <span className="vs-bar-fill" style={{ width: `${(r.n / max) * 100}%` }} />
          </span>
          <span className="vs-bar-n">{r.n}</span>
        </li>
      ))}
      <li className="vs-bars-label">{label}</li>
    </ul>
  );
}

// 具体去了哪些页：直接列出路径，太多就先给前三个，点一下展开
function Pages({ pages }) {
  const [open, setOpen] = useState(false);
  const views = pages.reduce((a, p) => a + p.n, 0);
  const shown = open || pages.length <= 4 ? pages : pages.slice(0, 3);
  return (
    <>
      <span className="vs-dim">
        {pages.length} page{pages.length > 1 ? 's' : ''}{views > pages.length && ` · ${views} views`}
      </span>
      <ul className="vs-paths">
        {shown.map(p => (
          <li key={p.path} className={p.ok === false ? 'vs-fake' : ''}
              title={p.ok === false ? 'This page does not exist on the site' : undefined}>
            {p.path === '/' ? '/ (home)' : p.path}
            {p.n > 1 && <span className="vs-dim"> ×{p.n}</span>}
          </li>
        ))}
      </ul>
      {pages.length > shown.length && (
        <button type="button" className="vs-link" onClick={() => setOpen(true)}>
          show all {pages.length}
        </button>
      )}
    </>
  );
}

function VisitTable({ rows, machines }) {
  if (!rows.length) return <p className="vs-empty">—</p>;
  return (
    <div className="vs-table-wrap">
      <table className="vs-table">
        <thead>
          <tr>
            <th>When</th>{machines && <th>Type</th>}<th>Where</th><th>Pages visited</th>
            <th title="The page the visitor says it came from. Programs often make this up.">Came from</th><th>Agent</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(sess => (
            <tr key={sess.key}>
              <td className="vs-nowrap">
                {fmt(sess.last)}
                {sess.first !== sess.last && <span className="vs-dim"> · {span(sess.first, sess.last)}</span>}
              </td>
              {machines && (
                <td>
                  <span className={`vs-kind vs-kind-${sess.kind}`}>{sess.kind === 'scanner' ? 'Scanner' : 'Bot'}</span>
                  <div className="vs-why">{sess.why}</div>
                </td>
              )}
              <td>{sess.place}</td>
              <td className="vs-pages"><Pages pages={sess.pages} /></td>
              <td className="vs-dim">{sess.ref ? host(sess.ref) : '—'}</td>
              <td className="vs-dim vs-ua" title={sess.ua}>{sess.ua}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Visits() {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  const load = useCallback(async (d) => {
    try {
      const res = await visitsAPI.get(d);
      setData(res.data);
      setErr('');
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('token'); setToken(null); }
      else setErr('Could not load stats.');
    }
  }, []);

  useEffect(() => { if (token) load(days); }, [token, days, load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await authAPI.login(password);
      localStorage.setItem('token', res.data.token);
      setToken(res.data.token);
      setPassword('');
    } catch {
      setErr('Wrong password.');
    }
  };

  if (!token) {
    return (
      <div className="page-container vs-page">
        <section className="page-header"><h1>Visitors</h1></section>
        <form className="vs-login" onSubmit={login}>
          <input
            type="password" value={password} autoFocus
            onChange={e => { setPassword(e.target.value); setErr(''); }}
            placeholder="Admin password" aria-label="Admin password"
          />
          <button type="submit" className="btn btn-cta">Sign in</button>
          {err && <p className="vs-err">{err}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="page-container vs-page">
      <section className="page-header vs-head">
        <h1>Visitors</h1>
        <div className="vs-range">
          {[7, 30, 90, 365].map(d => (
            <button
              type="button" key={d}
              className={`vs-range-btn${days === d ? ' is-active' : ''}`}
              onClick={() => setDays(d)}
            >{d}d</button>
          ))}
          <button type="button" className="vs-range-btn" onClick={() => {
            localStorage.removeItem('token'); setToken(null);
          }}>Sign out</button>
        </div>
      </section>

      {err && <p className="vs-err">{err}</p>}
      {!data ? <p className="vs-empty">Loading…</p> : (
        <section className="page-content">
          <div className="vs-tiles">
            <div className="vs-tile"><strong>{data.inRange}</strong><span>human views · {data.days}d</span></div>
            <div className="vs-tile"><strong>{data.visitors}</strong><span>visitors</span></div>
            <div className="vs-tile"><strong>{data.total}</strong><span>human views all time</span></div>
            {(data.botVisits != null) && (
              <div className="vs-tile vs-tile-bot">
                <strong>{(data.botVisits || 0) + (data.scannerVisits || 0)}</strong>
                <span>machine visits · {data.days}d</span>
              </div>
            )}
          </div>

          <p className="vs-note">
            No IP addresses are stored. Each address is turned into a city in memory and
            discarded; the lookup is offline, so nothing about a visitor leaves the server.
            Counts and charts include people only. <b>Bot</b> means the visitor said it is a program
            (Googlebot, ChatGPT, curl …) or kept changing its browser identity. <b>Scanner</b> means it
            tried folders this site does not have, such as /wp/ or /wordpress/, looking for a hackable
            blog. Neither can see anything behind a password: they only get the empty page shell.
            <b> Unknown</b> place means the address belongs to a data centre or cloud server that the
            offline map has no city for, which usually means a program rather than a person.
          </p>

          <div className="vs-grid">
            <div><h2>Per day</h2><Bars rows={data.byDay} label="date · views" /></div>
            <div><h2>Pages</h2><Bars rows={data.topPaths} label="path · views" /></div>
            <div><h2>Referrers</h2><Bars rows={data.topRefs} label="host · views" /></div>
            <div><h2>Places</h2><Bars rows={data.topPlaces} label="city · views" /></div>
          </div>

          {(() => {
            const people = data.sessions?.filter(x => !x.kind || x.kind === 'human') || [];
            const machines = data.sessions?.filter(x => x.kind && x.kind !== 'human') || [];
            return (
              <>
                <h2>People · recent visits</h2>
                <p className="vs-hint">One row per visitor per day.</p>
                <VisitTable rows={people} />

                <details className="vs-machines">
                  <summary>
                    Bots &amp; scanners · latest {machines.length} <span className="vs-dim">(click to open or close)</span>
                  </summary>
                  <p className="vs-hint">
                    Programs, not people. Coloured pages do not exist on this site: the program was guessing.
                  </p>
                  <VisitTable rows={machines} machines />
                </details>
              </>
            );
          })()}
        </section>
      )}
    </div>
  );
}

export default Visits;
